import { ClipboardEvent, RefObject } from 'react';
import axiosConfig from 'src/Resources/axiosConfig';

export interface PendingPaste {
  placeholderId: string;
  blob: File;
  blobUrl: string;
}

interface ImmediateOptions {
  mode: 'immediate';
  ticketId?: number;
  ticketCommentId?: number;
}

interface DeferredOptions {
  mode: 'deferred';
  onBufferedPaste: (paste: PendingPaste) => void;
}

type Options = (ImmediateOptions | DeferredOptions) & {
  textareaRef: RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  value: string;
  onChange: (next: string) => void;
  onError?: (kind: 'tooLarge' | 'uploadFailed') => void;
};

const MAX_BYTES = 100 * 1024 * 1024;

function extensionFor(type: string): string {
  if (type === 'image/png') return 'png';
  if (type === 'image/jpeg') return 'jpg';
  if (type === 'image/gif') return 'gif';
  if (type === 'image/webp') return 'webp';
  return 'png';
}

function makeFilename(type: string): string {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-');
  return `pasted-${stamp}.${extensionFor(type)}`;
}

function spliceAtCursor(
  el: HTMLInputElement | HTMLTextAreaElement | null,
  value: string,
  insertion: string
): { next: string; caret: number } {
  const start = el?.selectionStart ?? value.length;
  const end = el?.selectionEnd ?? value.length;
  const before = value.slice(0, start);
  const after = value.slice(end);
  const needsLeadingNl = before.length > 0 && !before.endsWith('\n');
  const needsTrailingNl = after.length > 0 && !after.startsWith('\n');
  const wrapped =
    (needsLeadingNl ? '\n' : '') + insertion + (needsTrailingNl ? '\n' : '');
  const next = before + wrapped + after;
  const caret = before.length + wrapped.length;
  return { next, caret };
}

export function usePasteImage(opts: Options) {
  const { textareaRef, value, onChange, onError } = opts;

  return async (e: ClipboardEvent<HTMLDivElement | HTMLInputElement | HTMLTextAreaElement>) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      let imageFile: File | null = null;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const f = item.getAsFile();
          if (f) {
            imageFile = f;
            break;
          }
        }
      }
      if (!imageFile) return;

      e.preventDefault();

      if (imageFile.size > MAX_BYTES) {
        onError?.('tooLarge');
        return;
      }

      const filename = makeFilename(imageFile.type);
      const renamed = new File([imageFile], filename, { type: imageFile.type });

      if (opts.mode === 'deferred') {
        const blobUrl = window.URL.createObjectURL(renamed);
        const placeholderId = `paste-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        opts.onBufferedPaste({ placeholderId, blob: renamed, blobUrl });
        const insertion = `![${placeholderId}](${blobUrl})`;
        const { next, caret } = spliceAtCursor(textareaRef.current, value, insertion);
        onChange(next);
        requestAnimationFrame(() => {
          const el = textareaRef.current;
          if (!el) return;
          el.focus();
          el.setSelectionRange(caret, caret);
        });
        return;
      }

      // Immediate mode: upload now and splice the real /DownloadDocument URL
      try {
        const formData = new FormData();
        formData.append('file', renamed);
        const res = await axiosConfig.post('/UploadDocument', formData, {
          params: {
            scope: 0,
            ticketId: opts.ticketId,
            ticketCommentId: opts.ticketCommentId
          },
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const docId = res.data?.id;
        if (!docId) {
          onError?.('uploadFailed');
          return;
        }
        const insertion = `![](/DownloadDocument?id=${docId})`;
        const { next, caret } = spliceAtCursor(textareaRef.current, value, insertion);
        onChange(next);
        requestAnimationFrame(() => {
          const el = textareaRef.current;
          if (!el) return;
          el.focus();
          el.setSelectionRange(caret, caret);
        });
      } catch (err: any) {
        console.warn('[Paste] Upload failed:', err?.response?.data || err?.message);
        onError?.('uploadFailed');
      }
  };
}
